import { AUTH_STRINGS } from '../constant/stringConstant';

/**
 * Helper function to extract error message from API response
 * @param error - The error object from API response
 * @param defaultMessage - Default message to use if no specific error found
 * @returns Extracted error message string
 */
// Recursively search for the deepest error message in any nested structure
function findDeepestMessage(obj: any): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  let currentMessage: string | undefined;
  // Check known keys at this level
  if (typeof obj.message === 'string' && obj.message.trim()) currentMessage = obj.message;
  if (obj.Status) {
    if (typeof obj.Status.DisplayMessage === 'string' && obj.Status.DisplayMessage.trim()) currentMessage = obj.Status.DisplayMessage;
    if (typeof obj.Status.InternalErrorMessage === 'string' && obj.Status.InternalErrorMessage.trim()) currentMessage = obj.Status.InternalErrorMessage;
  }
  // Recursively search all object properties for a deeper message
  let deepestMessage: string | undefined = undefined;
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (typeof val === 'object' && val !== null) {
      const found = findDeepestMessage(val);
      if (found) deepestMessage = found;
    }
  }
  // Prefer the deepest message found, otherwise the current level's message
  return deepestMessage || currentMessage;
}

export const extractErrorMessage = (error: any, defaultMessage: string): string => {
  if (!error) return defaultMessage;
  const msg = findDeepestMessage(error);
  return typeof msg === 'string' && msg.trim() ? msg : defaultMessage;
};

/**
 * Helper function to get appropriate error message based on HTTP status code
 * @param error - The error object from API response
 * @returns Appropriate error message from API or fallback based on status code
 */
export const getLoginErrorMessage = (error: any): string => {
  // First try to extract the actual API error message
  const apiErrorMessage = extractErrorMessage(error, '');
  
  // If we have a meaningful API error message, use it
  if (apiErrorMessage && apiErrorMessage !== '') {
    return apiErrorMessage;
  }
  
  // Fallback to status code based messages only if no API message is available
  switch (error?.response?.status) {
    case 400:
      return AUTH_STRINGS.INVALID_CREDENTIALS_ERROR;
    case 401:
      return AUTH_STRINGS.INVALID_CREDENTIALS_ERROR;
    case 404:
      return AUTH_STRINGS.USER_NOT_FOUND_ERROR;
    default:
      return AUTH_STRINGS.LOGIN_FAILED_ERROR;
  }
};

/**
 * Validates login form inputs
 * @param username - Username/email input
 * @param password - Password input
 * @returns Object with validation result and error message
 */
export const validateLoginForm = (username: string, password: string): { isValid: boolean; errorMessage?: string } => {
  if (!username.trim()) {
    return { isValid: false, errorMessage: AUTH_STRINGS.EMAIL_REQUIRED_ERROR };
  }
  if (!password.trim()) {
    return { isValid: false, errorMessage: AUTH_STRINGS.PASSWORD_REQUIRED_ERROR };
  }
  return { isValid: true };
};

/**
 * Validates OTP input
 * @param otpString - OTP string to validate
 * @param requiredLength - Required length of OTP (default: 4)
 * @returns Object with validation result and error message
 */
export const validateOtp = (otpString: string, requiredLength: number = 4): { isValid: boolean; errorMessage?: string } => {
  if (otpString.length !== requiredLength) {
    return { isValid: false, errorMessage: AUTH_STRINGS.OTP_REQUIRED_ERROR };
  }
  return { isValid: true };
};

/**
 * Checks if API response indicates successful OTP sending
 * @param response - API response object
 * @returns Boolean indicating if OTP was sent successfully
 */
export const isOtpSentSuccessfully = (response: any): boolean => {
  return response.response?.data?.data?.success || response?.Data?.IsOtpSent;
};

/**
 * Extracts error message from OTP sending response
 * @param response - API response object
 * @returns Error message from response or default
 */
export const getOtpSendErrorMessage = (response: any): string => {
  // Try to extract the actual API error message first
  const apiErrorMessage = extractErrorMessage(response, '');
  
  if (apiErrorMessage && apiErrorMessage !== '') {
    return apiErrorMessage;
  }
  
  // Legacy structure for backward compatibility
  const legacyMessage = response.response?.data?.data?.message || 
                       response.response?.data?.message;
  
  if (legacyMessage) {
    return legacyMessage;
  }
  
  // Fallback to default
  return AUTH_STRINGS.INVALID_CREDENTIALS_ERROR;
};

/**
 * Checks if login verification was successful
 * @param response - API response object
 * @returns Boolean indicating if login was successful
 */
export const isLoginSuccessful = (response: any): boolean => {
  return response.status === "success";
};

/**
 * Helper function to get appropriate error message for OTP verification failures
 * @param error - The error object from API response
 * @returns Appropriate error message from API or fallback
 */
export const getOtpVerificationErrorMessage = (error: any): string => {
  // First try to extract the actual API error message
  const apiErrorMessage = extractErrorMessage(error, '');
  
  // If we have a meaningful API error message, use it
  if (apiErrorMessage && apiErrorMessage !== '') {
    return apiErrorMessage;
  }
  
  // Fallback to default OTP error message
  return AUTH_STRINGS.INVALID_OTP_ERROR;
};

/**
 * Helper function to get appropriate error message for resend OTP failures
 * @param error - The error object from API response
 * @returns Appropriate error message from API or fallback
 */
export const getResendOtpErrorMessage = (error: any): string => {
  // First try to extract the actual API error message
  const apiErrorMessage = extractErrorMessage(error, '');
  
  // If we have a meaningful API error message, use it
  if (apiErrorMessage && apiErrorMessage !== '') {
    return apiErrorMessage;
  }
  
  // Fallback to default resend OTP error message
  return AUTH_STRINGS.RESEND_OTP_FAILED_ERROR;
};

/**
 * Constants for login functionality
 */
export const LOGIN_CONSTANTS = {
  OTP_LENGTH: 4,
  INITIAL_OTP: Array(4).fill(""),
  STEPS: {
    LOGIN: "login" as const,
    OTP: "otp" as const
  }
} as const;
